<template>
    <div class="exam-result-management">
        <!-- 검색/필터 영역 -->
        <Search :filter="filter" :options="options" @search="fetchExamResults" @reset="onResetFilter"/>
        <div class="result-section">
            <div class="result-header">
                <!-- 목록 정렬 드롭다운 -->
                <div class="list-sort">
                    <select v-model="sortOption" @change="fetchExamResults(1)">
                        <option value="">최근 등록일</option>
                        <option value="recently">최근 실행일</option>
                        <option value="patientNumber">환자번호</option>
                        <option value="patientName">환자명</option>
                        <option value="birthDate">생년월일</option>
                    </select>
                </div>
                <div class="action-buttons">
                    <button class="card-button" @click="onDiagnose">통증 측정
                        <img src="@/assets/resource/svg/diagnosis.svg" alt="통증 측정" />
                    </button>
                    <button class="card-button" @click="onPrescribe">통증 케어
                        <img src="@/assets/resource/icon/RX_W.png" alt="통증 케어" />
                    </button>
                </div>    
            </div>
            <!-- 결과 목록 테이블 -->
            <table class="result-table">
                <colgroup>
                    <col style="width: 5%;">
                    <col style="width: 18%;">
                    <col style="width: auto;">
                    <col style="width: 18%;">
                    <col style="width: 18%;">
                    <col style="width: 15%;">
                    <col style="width: 15%;">
                </colgroup>
                <thead>
                    <tr>
                        <th>
                            <!-- <input type="checkbox" v-model="allSelected" @change="toggleSelectAll"/> -->
                        </th>
                        <th>환자번호</th>
                        <th>환자명</th>
                        <th>생년월일</th>
                        <th>최근 실행일</th>
                        <th>통증 측정 이력</th>
                        <th>통증 케어 이력</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="protocolList.length === 0">
                        <td colspan="6" style="color:#9ab2ca; text-align: center;">
                            데이터가 없습니다.
                        </td>
                    </tr>
                    <tr v-else v-for="(row) in protocolList" :key="row.connection_id"
                        @click="toggleRowSelect(row.patientId)"
                        :class="{ selected: selectedRows.includes(row.patientId)}"
                        >
                        <td>
                            <input type="checkbox" v-model="selectedRows" :value="row.patientId"/>
                        </td>
                        <td>{{ row.patientId }}</td>
                        <td>{{ row.name }}</td>
                        <td>{{ row.dob }}</td>
        <!--                <td>{{ dateUtils.formatDate(row.createdAt) }}</td>-->
                        <td>{{ dateUtils.formatDate(row.prescribedDate) }}</td>
                        <td><button @click="viewDiagnosisDetail(row)" class="detail-btn">통증 이력</button></td>
                        <td><button @click="viewPrescriptionDetail(row)" class="detail-btn">케어 이력</button></td>
                    </tr>
                </tbody>
            </table>
            <pagination
                :total-items="totalCount"
                :page-size="pageSize"
                v-model="currentPage"
                @page-change="changePage"
            />
        </div>
    </div>
</template>
<script setup>
    defineOptions({
        meta: {
            title : '환자 목록',
            filter:'PatientListFilter',
            page  :'PatientListPage',
        }
    })

    import api, { urls } from "@/api";
    import { dateUtils } from "@/utils/dateUtils";
    import Pagination    from "@/components/common/Pagination.vue";
    import Search        from "../common/Search.vue";
    import { reactive, ref, onMounted, watch } from 'vue'
    import {useRouter}      from "vue-router";
    import { useAuthStore } from '@/stores/auth';
    import { useAppAlert }  from '@/composables/useAppAlert'

    const { showAlert, showAlertAndMove, confirm } = useAppAlert()

    const options = [
        { value: 'patientNumber', label: '환자번호' },
        { value: 'patientName', label: '환자명' }
    ]
    const router = useRouter()
    const filter = reactive({
        searchType: "all", // patientName or patientNo
        searchText: "",
        startDate : "",
        endDate   : "",
    })

    const sortOption    = ref("")
    const protocolList  = ref([])
    const currentPage   = ref(Number(sessionStorage.getItem('PatientListPage') || 1));
    const pageSize      = ref(10)
    const totalPages    = ref(1)
    const totalCount    = ref(0)

    // 선택된 행들의 ID 목록
    const selectedRows = ref([])

    // 전체선택 체크박스
    const allSelected = ref(false)

    // 전체선택/해제
    const toggleSelectAll = () => {
        if (allSelected.value) {
            // 현재 표시된 모든 row의 id를 선택
            selectedRows.value = protocolList.value.map(row => row.patientId)
        } else {
            selectedRows.value = []
        }
    }

    const changePage = (page) => {
        fetchExamResults(page)
        selectedRows.value = []
    }

    const fetchExamResults = async (page = 1) => {
        if (filter.startDate && filter.endDate) {
            const start = new Date(filter.startDate);
            const end   = new Date(filter.endDate);

            if (start > end) {
                showAlert('시작일자는 종료일자보다 작거나 같아야 합니다.');
                return; // 검색 실행하지 않음
            }
        }

        try {
            const authStore = useAuthStore()
            const inst_cd   = authStore.getUser?.inst_cd

            const params = {
                ...(filter.startDate && {startDate: filter.startDate + ' 00:00:00'}),
                ...(filter.endDate && {endDate: filter.endDate + ' 23:59:59'}),
                orderType: sortOption.value,
                page: currentPage.value,
                pageRow: pageSize.value,
                instCd: inst_cd
            };

            if(filter.searchType == 'all'){
                params.all = filter.searchText
            }

            if(filter.searchType == 'patientNumber'){
                params.patientId = filter.searchText
            }

            if(filter.searchType == 'patientName'){
                params.name = filter.searchText
            }

            const res = await api.get(urls.diagnosis.result, { params: params });

            // 서버에서 받은 데이터 구조에 따라 조정 가능
            protocolList.value  = res.data.list || res.data || [];
            totalCount.value    = res.data.totCnt || 0
            totalPages.value    = Math.ceil(totalCount.value / pageSize.value);
            currentPage.value   = page;

        } catch (err) {
            protocolList.value  = [];
            totalCount.value    = 0;
            totalPages.value    = 1;
            currentPage.value   = 1;
        }
    }
    const viewDiagnosisDetail = (row) => {
        router.push({
            path: `/patientlist/diagnosisHistory/${row.patientId}`
        })
    }

    const viewPrescriptionDetail = (row) => {
        router.push({
            path: `/patientlist/prescriptionHistory/${row.patientId}`
        })
    }

    const onDiagnose = () => {
        if (selectedRows.value.length === 0) {
            showAlert('환자를 선택하세요.');
            return;
        }
        if (selectedRows.value.length > 1) {
            showAlert('1명의 환자만 선택하세요.');
            return;
        }

        // 예시: 첫 번째 선택 환자만 이동
        const targetId = selectedRows.value[0]
        router.push({
            path: `/patientlist/diagnosticsRun/${targetId}`
        })
    }

    const onPrescribe = () => {
        if (selectedRows.value.length === 0) {
            showAlert('환자를 선택하세요.');
            return;
        }
        if (selectedRows.value.length > 1) {
            showAlert('1명의 환자만 선택하세요.');
            return;
        }

        // 예시: 첫 번째 선택 환자만 이동
        const targetId = selectedRows.value[0]
        router.push({
            path: `/patientlist/prescriptionRun/${targetId}`
        })
    }

    function goBack() {
        router.back()
    }

    function toggleRowSelect(patientId) {
        if (selectedRows.value[0] === patientId) {
            selectedRows.value = [];
        } else {
            selectedRows.value = [patientId];
        }
    }

    const onResetFilter = () => {
        // 필터 값 초기화
        filter.searchType = "all";
        filter.searchText = "";
        filter.startDate  = "";
        filter.endDate    = "";

        // 1페이지부터 다시 데이터 조회
        // fetchExamResults(1);
    };
    const PrescriptionListFilter = {
        searchType  : "all",
        searchText  : "",
        startDate   : "",
        endDate     : ""
    };

    const DiagnosisListFilter = {
        searchType  : "all",
        searchText  : "",
        startDate   : "",
        endDate     : ""
    };

    const filterStorageKey = 'PatientListFilter';

    let watchFilterStop = null;
    onMounted(() => {
        const savedFilterString = sessionStorage.getItem(filterStorageKey);

        if (savedFilterString) {
            const savedFilter = JSON.parse(savedFilterString);
            filter.searchType = savedFilter.searchType || "all";
            filter.searchText = savedFilter.searchText || "";
            filter.startDate  = savedFilter.startDate || "";
            filter.endDate    = savedFilter.endDate || "";
        }

        watchFilterStop = watch(filter, (newVal) => {
            sessionStorage.setItem(filterStorageKey, JSON.stringify(newVal));
        }, { deep: true });

        fetchExamResults(currentPage.value);
        sessionStorage.setItem('PrescriptionListPage', '1');
        sessionStorage.setItem('PrescriptionListFilter', JSON.stringify(PrescriptionListFilter));
        sessionStorage.setItem('DiagnosisListPage', '1');
        sessionStorage.setItem('DiagnosisListFilter', JSON.stringify(DiagnosisListFilter));
    })

    watch(selectedRows, () => {
        allSelected.value =
            selectedRows.value.length > 0 &&
            selectedRows.value.length === protocolList.value.length
    })

    watch(
        () => filter.startDate,
        (newStart, oldStart) => {
            if (newStart && filter.endDate) {
                if (new Date(newStart) > new Date(filter.endDate)) {
                    showAlert('시작일자는 종료일자보다 작거나 같아야 합니다.');
                    filter.startDate = oldStart || '';
                }
            }
        }
    )

    watch(
        () => filter.endDate,
        (newEnd, oldEnd) => {
            if (newEnd && filter.startDate) {
                if (new Date(newEnd) < new Date(filter.startDate)) {
                    showAlert('종료일자는 시작일자보다 크거나 같아야 합니다.');
                    filter.endDate = oldEnd || '';
                }
            }
        }
    )
</script>
